import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";
import { ClipboardList, Loader2, RefreshCw, ChevronRight, Inbox } from "lucide-react";
import { ClinicRequestDetailModal } from "./ClinicScheduling";
import { tintMapFor, tintOf } from "../constants/locationTints";
import { locationLabelMap } from "../components/locationColors";
import { useLocationFilter, matchesLocationFilter } from "../lib/clinicLocationFilter";
import { fmtRange } from "../components/proposalRange";

function formatApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return JSON.stringify(detail);
}
const fmtDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

// ── Pending Requests list (Clinic Portal) ───────────────────────────────────
// All `status='pending'` requests for this clinic, oldest-first. Clicking a
// row opens the shared detail modal where the clinic confirms or counter-
// proposes. Detail-modal actions trigger `refresh()` here and also fire the
// global "clinic-pending-refresh" event so the sidebar badge updates.
//
// D.1/D.2/D.4/D.5 (2026-06): a LOCATION column with the roster name + hue,
// the shared session location filter (DISPLAY ONLY — the fetch below is
// unchanged and carries no location parameter), full window RANGES and a
// "+N more" indicator instead of silently truncating at three.
export default function ClinicPendingRequests() {
  const { refreshPending, profile } = useOutletContext() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await api.get("/scheduling/clinic/pending");
      setItems(res.data?.requests || []);
    } catch (e) {
      setErr(formatApiError(e, "Failed to load pending requests"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChanged = () => {
    load();
    refreshPending?.();
    window.dispatchEvent(new CustomEvent("clinic-pending-refresh"));
  };

  const profileLocations = useMemo(() => profile?.locations || [], [profile]);
  const isMultiLocation = profileLocations.length > 1;
  const labels = useMemo(() => locationLabelMap(profileLocations), [profileLocations]);
  // HOTFIX 2026-09-18 — the hue comes from the ONE sorted-rank assignment
  // map derived from this login's locations, never from a hash of a code.
  const tintMap = useMemo(() => tintMapFor(profile), [profile]);
  const { selected: locFilter, isAll: locFilterIsAll, toggle: toggleLocation, showAll: showAllLocations } =
    useLocationFilter();
  const rows = useMemo(
    () => items.filter((r) => matchesLocationFilter(locFilter, r.location_code)),
    [items, locFilter]
  );

  return (
    <div className="p-6 space-y-5" data-testid="clinic-pending-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList size={22} className="text-amber-600" />
            Pending Requests
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">WestStar is asking your team to confirm or counter-propose a time.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="clinic-pending-refresh-btn">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {isMultiLocation && (
        <div
          data-testid="pending-location-filter"
          className="flex items-center gap-2 flex-wrap px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
        >
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Locations</span>
          <button
            type="button"
            data-testid="pending-filter-all"
            aria-pressed={locFilterIsAll}
            onClick={showAllLocations}
            className={`text-xs px-2 py-1 rounded-full border transition ${
              locFilterIsAll
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
            }`}
          >
            All
          </button>
          {profileLocations.map((l) => {
            const t = tintOf(tintMap, l.location_code);
            const on = matchesLocationFilter(locFilter, l.location_code);
            return (
              <button
                key={l.location_code}
                type="button"
                data-testid={`pending-filter-${l.location_code}`}
                aria-pressed={on}
                onClick={() => toggleLocation(l.location_code)}
                className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border transition ${
                  on ? "shadow-sm" : "bg-white text-slate-400 border-slate-200 opacity-60 hover:opacity-100"
                }`}
                style={on && t ? { backgroundColor: t.tint, color: t.text, borderColor: t.border } : undefined}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t?.border }} />
                {labels[l.location_code] || l.location_code}
              </button>
            );
          })}
        </div>
      )}

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="clinic-pending-empty">
            <Inbox size={32} className="mx-auto mb-2 text-slate-300" />
            <div className="text-lg font-semibold text-slate-700">No pending requests</div>
            <div className="text-sm">
              {items.length > 0
                ? "None at the locations you're showing — switch a location chip back on."
                : "All caught up! WestStar will let you know when there's something new."}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Patient", "Location", "Type", "Proposed Times", "Submitted", "Notes", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => {
                // `source !== "network"` rather than `=== "weststar"`:
                // patient-submitted windows (Contract §11 Commit 3) are OUR
                // side of the conversation and must reach the clinic exactly
                // like a staff proposal — filtering on "weststar" would have
                // hidden them entirely.
                const windows = (r.proposed_times || []).filter(p => p.source !== "network");
                const t = tintOf(tintMap, r.location_code);
                const hidden = Math.max(windows.length - 3, 0);
                return (
                  <tr
                    key={r.id}
                    data-testid={`pending-row-${r.id}`}
                    onClick={() => setActive(r)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{r.patient_name || `Patient #${r.patient_id}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`pending-loc-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border"
                        style={t ? { backgroundColor: t.tint, color: t.text, borderColor: t.border } : undefined}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t?.border }} />
                        {labels[r.location_code] || r.location_code || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{r.appointment_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-slate-600 space-y-0.5" data-testid={`pending-windows-${r.id}`}>
                        {windows.slice(0, 3).map(p => (
                          <div key={p.id} className="whitespace-nowrap">
                            {fmtDay(p.date)}{" · "}{fmtRange(p)}
                          </div>
                        ))}
                        {hidden > 0 && (
                          <div data-testid={`pending-more-${r.id}`} className="text-[11px] font-semibold text-slate-500">
                            +{hidden} more
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      {r.notes ? (
                        <span className="text-xs italic text-slate-500 truncate block" title={r.notes}>{r.notes}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={14} className="text-slate-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ClinicRequestDetailModal
        open={!!active}
        request={active}
        profile={profile}
        onClose={() => setActive(null)}
        onConfirmed={onChanged}
        onSuggested={onChanged}
      />
    </div>
  );
}
